// web-v2/src/lib/moneyStrip.js
//
// Pure money-strip derivation off the policy_transactions ledger (invoiced /
// paid / owed). Extracted from MoneyStrip.jsx so the math is unit-testable.

const SETTLED_CASH = 'succeeded';
const WAIVED = 'waived';

// A bind created by the tower binding flow that the client has NOT signed (no
// signed application on file) and that carries no A/R yet is PROVISIONAL: the
// client merely selected a quote, or a dry run created it, but never completed
// the binding process. It must not read as invoiced/owed — nothing is billed
// until the client signs (or pays). It "lands" on the ledger at that point.
//
// Broker-established baselines and manual binds have no session_id, so they are
// never treated as provisional. A signed bind (our_pdf_workdrive_id set) or one
// with any A/R activity (ar_payment_status !== 'na') has landed and counts.
export function isProvisionalBind(t) {
  return t
    && t.type === 'bind'
    && !!t.session_id
    && !t.our_pdf_workdrive_id
    && String(t.ar_payment_status || 'na').toLowerCase() === 'na';
}

export function deriveMoneyStrip(txns) {
  const live = (txns || []).filter((t) => !t.voided_at);
  // Provisional (unsigned, unpaid tower) binds are not on the money ledger yet.
  const committed = live.filter((t) => !isProvisionalBind(t));
  // A row whose premium_delta is NULL has an UNKNOWN amount (a signed
  // cancellation awaiting the carrier's return premium). It contributes
  // nothing to the totals because there is nothing to contribute, but the
  // strip must be able to say the totals are provisional rather than final,
  // so it is counted rather than silently absorbed as a zero.
  // CC PROMPT 56 item 1.
  const unknownMoney = committed.filter((t) => t.premium_delta == null).length;
  // What the client is billed on the row: premium + tax + the carrier's program fee + our fee (utils/ledger-money.js
  // billedOf on the server; the carrier fee is carrier-fee-01, David 2026-10-31).
  const delta = (t) => Number(t.premium_delta || 0) + Number(t.tax_delta || 0) + Number(t.carrier_fee_delta || 0) + Number(t.fee_delta || 0);
  let invoiced = 0;
  let paid = 0;
  let waived = 0;
  let pending = 0;
  // Deferred (David 2026-08-12): a broker's deliberate hold. It stays in
  // OWED — it appears wherever unpaid appears — and carries its reason so
  // the strip can say WHY it is unpaid without the client ever hearing it.
  let deferred = 0;
  const deferrals = [];
  // Financed (190): the finance company paid, so the client owes nothing and
  // NDEMNIFLY received nothing. Out of OWED, its own tile, never inside Paid.
  let financed = 0;
  const financings = [];
  // Absorbed (David 2026-09-28): the client paid short and the agency ate the
  // difference. Out of OWED (nobody is owed it), its own tile, never in Paid.
  let absorbed = 0;
  const absorptions = [];
  const payments = [];
  for (const t of committed) {
    invoiced += delta(t);
    const ar = String(t.ar_payment_status || 'na').toLowerCase();
    if (ar === SETTLED_CASH) {
      const amt = Number.isFinite(Number(t.ar_paid_amount)) ? Number(t.ar_paid_amount) : delta(t);
      paid += amt;
      payments.push({ amount: amt, method: t.ar_payment_method || 'unknown', at: t.ar_paid_on || t.ar_paid_at });
      if (Number.isFinite(Number(t.ar_shortfall_absorbed)) && Number(t.ar_shortfall_absorbed) > 0) {
        absorbed += Number(t.ar_shortfall_absorbed);
        absorptions.push({ amount: Number(t.ar_shortfall_absorbed), reason: t.ar_shortfall_reason || '' });
      }
    } else if (ar === WAIVED) {
      waived += delta(t);
    } else if (ar === 'pending') {
      const amt = Number.isFinite(Number(t.ar_paid_amount)) ? Number(t.ar_paid_amount) : delta(t);
      pending += amt;
      payments.push({ amount: amt, method: t.ar_payment_method || 'unknown', at: t.ar_paid_on || t.ar_paid_at, pending: true });
    } else if (ar === 'deferred') {
      deferred += delta(t);
      deferrals.push({ amount: delta(t), reason: t.ar_deferred_reason || '' });
    } else if (ar === 'financed') {
      financed += delta(t);
      financings.push({ amount: delta(t), company: t.ar_financed_by || '', ref: t.ar_payment_ref || '', on: t.ar_paid_on || null });
    }
  }
  const owed = Math.round((invoiced - paid - waived - financed - absorbed) * 100) / 100;
  // hasLedger reflects COMMITTED rows only: a deal whose sole transaction is a
  // provisional bind shows the "lands at bind" empty state, not a row of zeros.
  return {
    invoiced, paid, waived, pending, owed, payments,
    hasLedger: committed.length > 0,
    unknownMoney,
    provisional: unknownMoney > 0,
    deferred,
    deferrals,
    financed,
    financings,
    absorbed,
    absorptions,
  };
}
